import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {cn} from '../../utils/helper';
import LogoImg from '../../assets/images/logo/logo_black.svg';
import LeftArrowIcon from '../../assets/images/icon/left_arrow_icon.svg';
import ExternalArrowIcon from '../../assets/images/icon/mobile_angle_icon.png';
import {GUIDE_NAV_ITEMS, type GuideNavItem} from './navConfig';
import './GuideMobileHeader.css';

/* ===================== 타입 ===================== */
type GuideMobileHeaderProps = {
    title: string;
    subtitle?: string;
    tabs: Array<{
        id: string;
        label: string;
        href: string;
        isExternal: boolean;
    }>;
    activeId?: string;
};

/* ===================== 아이콘 (파일 내부 정의) ===================== */
const ChevronDownIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg
        className={className}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({className = ''}) => (
    <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M9 2.5a6.5 6.5 0 015.165 10.35l3.492 3.493a.75.75 0 01-1.06 1.06l-3.493-3.492A6.5 6.5 0 119 2.5zm0 1.5a5 5 0 100 10 5 5 0 000-10z"
            fill="currentColor"
        />
    </svg>
);

const PageIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg
        className={className}
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M7 3h7.5L19 7.5V21a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"/>
        <path d="M14.5 3.5V8h4.5"/>
    </svg>
);

const HashIcon: React.FC<{ className?: string }> = ({className}) => (
    <svg
        className={className}
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M5 9h14"/>
        <path d="M5 15h14"/>
        <path d="M9 5l-2 14"/>
        <path d="M17 5l-2 14"/>
    </svg>
);

const normalizeText = (value: string) => value.replace(/\s+/g, '').toLowerCase();

/* ===================== 모바일 메뉴 데이터 (Header와 동일 구조) ===================== */
type MobileItem =
    | { type: 'link'; label: string; href: string; external?: boolean }
    | { type: 'accordion'; label: string; items: Array<{ label: string; href: string }>; defaultOpen?: boolean };

const MOBILE_MENU: MobileItem[] = [
    {
        type: 'accordion',
        label: '분양의신 3원칙',
        items: [
            {label: '프리미엄 콘텐츠 & 성과 중심 광고 솔루션', href: '/guide/principles/premium'},
            {label: '올인원(All-in-One) 대행 시스템', href: '/guide/principles/all-in-one'},
            {label: '고객이 실제로 돈을 벌게 만드는 광고', href: '/guide/principles/profit'},
        ],
    },
    {type: 'link', label: '광고 운영 프로세스', href: '/guide/process'},
    {
        type: 'accordion',
        label: '콘텐츠 제작',
        items: [
            {label: '배너 & 홈페이지 콘텐츠', href: '/guide/content/banner'},
            {label: '분양의신 CF 배너 시스템', href: '/guide/content/cf'},
        ],
    },
    {type: 'link', label: '광고 상품 가이드', href: '/guide/product'},
    {type: 'link', label: '물건별 광고 가이드', href: '/guide/property'},
    {type: 'link', label: '광고 게재 위치', href: '/guide/placements'},
    {type: 'link', label: '자주 묻는 질문 (FAQ)', href: '/guide/faq'},
    // {type: 'link', label: '분양의신 1:1 맞춤 컨설팅 받기', href: 'https://분양의신.net/contact', external: true},
    // {type: 'link', label: '회사소개', href: 'https://분양의신.net/company', external: true},
    // {type: 'link', label: '지역별 포트폴리오', href: 'https://분양의신.net/portfolio', external: true},
    // {type: 'link', label: '컨설턴트', href: 'https://분양의신.net/consultant', external: true},
    // {type: 'link', label: '하이타겟', href: 'https://분양의신.net/hightarget', external: true},
    // {type: 'link', label: '광고인사이드', href: 'https://adperson-inside.com/', external: true},
    // {type: 'link', label: '그리팅', href: 'https://adperson.career.greetinghr.com/ko/main', external: true},
];

type SearchOption = {
    id: string;
    label: string;
    href?: string;
    isExternal: boolean;
    depth: number;
};

const flattenGuideNav = (items: GuideNavItem[], depth = 0): SearchOption[] =>
    items.flatMap((item) => {
        const current: SearchOption = {
            id: item.id,
            label: item.label,
            href: item.href,
            isExternal: Boolean(item.href && !item.href.startsWith('/')),
            depth,
        };
        const children = item.items ? flattenGuideNav(item.items, depth + 1) : [];
        return [current, ...children];
    });

const MobileSearchOverlay: React.FC<{
    open: boolean;
    query: string;
    results: SearchOption[];
    onQueryChange: (value: string) => void;
    onSelect: (item: SearchOption) => void;
    onClose: () => void;
    inputRef: React.RefObject<HTMLInputElement | null>;
}> = ({open, query, results, onQueryChange, onSelect, onClose, inputRef}) => {
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open, inputRef]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (!open) return;
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    return (
        <div
            className={cn(
                'fixed inset-0 z-[10000] bg-[rgba(33,37,41,0.4)] transition-opacity md:hidden',
                open ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            aria-hidden={!open}
        >
            <div className="absolute inset-0" onClick={onClose} aria-label="검색 닫기"/>

            <div className="relative z-[1] px-4 pt-4">
                <div className="mobile-guide-search mobile-guide-search--wide">
                    <input
                        ref={inputRef}
                        type="search"
                        value={query}
                        placeholder="검색어를 입력해 주세요"
                        onChange={(e) => onQueryChange(e.target.value)}
                    />
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#868e96]"/>
                    <button
                        type="button"
                        onClick={() => {
                            onQueryChange('');
                            if (inputRef.current) {
                                inputRef.current.focus();
                            }
                        }}
                        aria-label="검색어 지우기"
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#495057]"
                    >
                        <CloseIcon className="h-5 w-5"/>
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto rounded-md bg-white shadow-xl px-[12px] py-[1rem] mobile-search-results">
                    {query.trim().length === 0 && (
                        <div className="px-4 py-5 text-sm text-[#495057]">검색어를 입력해 주세요.</div>
                    )}
                    {query.trim().length > 0 && results.length === 0 && (
                        <div className="px-4 py-5 text-sm text-[#495057]">일치하는 메뉴가 없습니다.</div>
                    )}
                    {query.trim().length > 0 && results.length > 0 && (
                        <div className="mobile-search-results__label">메뉴</div>
                    )}
                    {results.map((item) => (
                        <button
                            key={`${item.id}-${item.depth}`}
                            type="button"
                            className={cn(
                                'flex w-full items-center gap-3 px-4 py-[12px] text-left transition hover:bg-slate-100',
                                item.depth > 0 ? 'pl-8' : 'pl-4',
                            )}
                            onClick={() => onSelect(item)}
                        >
                            {item.depth > 0 ? (
                                <HashIcon className="h-6 w-6 text-[#343a40]"/>
                            ) : (
                                <PageIcon className="h-6 w-6 text-[#212529]"/>
                            )}
                            <span
                                className={cn(
                                    'text-[15px] leading-tight font-[400]',
                                    'text-[#0D0D0D]',
                                )}
                            >
                                {item.label}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ===================== 파일 내부 햄버거 버튼 ===================== */
const HamburgerButton: React.FC<{
    open: boolean;
    onToggle: () => void;
    className?: string;
}> = ({open, onToggle, className}) => (
    <button
        type="button"
        className={cn(
            'relative ml-auto flex h-10 w-10 flex-col items-center justify-center gap-1 rounded-md text-slate-800 transition hover:bg-slate-100 md:hidden',
            className,
        )}
        aria-controls="mobile-menu"
        aria-expanded={open}
        aria-label={open ? '메뉴 닫기' : '메뉴 열기'}
        onClick={onToggle}
    >
        <span
            className={cn('h-[2px] w-6 bg-slate-700 transition-transform duration-300', open && 'translate-y-[6px] rotate-45')}/>
        <span
            className={cn('h-[2px] w-6 bg-slate-700 transition-opacity duration-200', open ? 'opacity-0' : 'opacity-100')}/>
        <span
            className={cn('h-[2px] w-6 bg-slate-700 transition-transform duration-300', open && '-translate-y-[6px] -rotate-45')}/>
    </button>
);

/* ===================== 파일 내부 모바일 메뉴 ===================== */
const MobileGuideMenu: React.FC<{
    open: boolean;
    onClose: () => void;
}> = ({open, onClose}) => {
    const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
        () =>
            MOBILE_MENU.reduce((acc, item) => {
                if (item.type === 'accordion') acc[item.label] = !!item.defaultOpen;
                return acc;
            }, {} as Record<string, boolean>),
    );

    // 바디 스크롤 잠금
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    // ESC 닫기
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const toggle = (label: string) => setOpenAccordions((s) => ({...s, [label]: !s[label]}));

    return (
        <>
            {/* 오버레이 */}
            <div
                className={cn(
                    'fixed inset-0 z-[9998] bg-black/40 transition-opacity md:hidden',
                    open ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
                onClick={onClose}
                aria-hidden
            />

            {/* 우측 패널 */}
            <aside
                className={cn(
                    'fixed inset-y-0 right-0 z-[9999] w-full translate-x-full bg-white shadow-xl transition-transform duration-300 md:hidden',
                    open && '!translate-x-0',
                )}
                role="dialog"
                aria-modal="true"
                id="mobile-menu"
            >
                {/* 상단 */}
                <div className="flex items-center justify-between py-3 border-b border-[#0D0D0D1A]">
                    <div className="pl-[20px] flex items-end gap-2">
                        <img src={LogoImg} alt="분양의신 로고" className="h-[20px] w-auto"/>
                        <span className="text-[14px] leading-none font-[700] text-[#0D0D0D]">가이드</span>
                    </div>
                    <button onClick={onClose} aria-label="메뉴 닫기"
                            className="rounded-md px-[20px] hover:bg-slate-100 focus:outline-none">
                        <CloseIcon className="h-7 w-7 text-slate-700"/>
                    </button>
                </div>

                {/* 리스트 */}
                <nav className="flex h-[calc(100dvh-48px)] flex-col text-[14px] bg-white pt-4">
                    <ul className="flex-1 overflow-y-auto px-4 py-2">
                        {MOBILE_MENU.map((item) => {
                            if (item.type === 'link') {
                                return (
                                    <li key={item.label}>
                                        <a
                                            href={item.href}
                                            className="flex items-center justify-between rounded-lg px-3 py-3 text-[#0D0D0D] hover:bg-slate-100"
                                            onClick={onClose}
                                        >
                                            <span className="font-[600]">{item.label}</span>
                                            {item.external && <img src={ExternalArrowIcon} alt=""/>}
                                        </a>
                                    </li>
                                );
                            }

                            const isOpen = openAccordions[item.label];
                            return (
                                <li key={item.label} className="rounded-lg">
                                    <button
                                        type="button"
                                        className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left hover:bg-slate-100"
                                        onClick={() => toggle(item.label)}
                                        aria-expanded={isOpen}
                                        aria-controls={`sec-${item.label}`}
                                    >
                                        <span className="font-[700] text-[#0D0D0D]">{item.label}</span>
                                        <ChevronDownIcon
                                            className={cn('h-4 w-4 text-slate-700 transition-transform', isOpen && 'rotate-180')}/>
                                    </button>

                                    <div
                                        id={`sec-${item.label}`}
                                        className={cn(
                                            'overflow-hidden transition-[grid-template-rows,opacity] duration-300',
                                            isOpen ? 'grid grid-rows-[1fr] opacity-100' : 'grid grid-rows-[0fr] opacity-0',
                                        )}
                                    >
                                        <div className="min-h-0">
                                            <ul className="space-y-1 pb-2 pl-4 pr-3">
                                                {item.items.map((child) => (
                                                    <li key={child.label}>
                                                        <a
                                                            href={child.href}
                                                            className="block rounded-md px-2 py-2 font-[500] text-[#000000B2] hover:bg-slate-100"
                                                            onClick={onClose}
                                                        >
                                                            {child.label}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </aside>
        </>
    );
};

/* ===================== GuideMobileHeader (햄버거 + 모바일 메뉴 포함) ===================== */
const GuideMobileHeader: React.FC<GuideMobileHeaderProps> = ({title, subtitle, tabs, activeId}) => {
    const navigate = useNavigate();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchOptions = useMemo(() => flattenGuideNav(GUIDE_NAV_ITEMS), []);
    const filteredResults = useMemo(() => {
        const normalized = normalizeText(searchQuery);
        if (!normalized) return [];
        return searchOptions.filter(
            (item) => item.href && normalizeText(item.label).includes(normalized),
        );
    }, [searchQuery, searchOptions]);

    const goBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/', {replace: true});
    };

    const handleOpenSearch = () => {
        setMobileNavOpen(false);
        setSearchOpen(true);
    };

    const handleCloseSearch = () => {
        setSearchOpen(false);
        setSearchQuery('');
    };

    const handleSelectResult = (item: SearchOption) => {
        handleCloseSearch();
        if (!item.href) return;
        if (item.isExternal) {
            window.location.href = item.href;
            return;
        }
        navigate(item.href);
    };

    return (
        <>
            <div
                className="bg-white z-[9999] fixed py-[12px] px-[20px] border-b border-[#0D0D0D1A] top-0 left-0 w-full">
                <div className={'flex items-center justify-between relative'}>
                    <button onClick={goBack} type="button" className="md:hidden">
                        <img src={LeftArrowIcon} alt="뒤로가기"/>
                    </button>

                    <a href="https://xn--sr3b21hbrc97b.net" aria-label="홈으로 이동"
                       className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2">
                        <div className="flex gap-2 items-end">
                            <img className="w-[60px] h-auto" src={LogoImg} alt="분양의신 로고"/>
                        </div>
                    </a>

                    <div className="flex items-center gap-1 ml-auto">
                        <button
                            type="button"
                            onClick={handleOpenSearch}
                            aria-label="메뉴 검색 열기"
                            className="flex h-10 w-10 items-center justify-center rounded-md text-[#212529] transition hover:bg-slate-100 md:hidden"
                        >
                            <SearchIcon className="h-6 w-6"/>
                        </button>
                        <HamburgerButton open={mobileNavOpen} onToggle={() => setMobileNavOpen((v) => !v)}/>
                    </div>
                </div>
            </div>

            <div className="space-y-6 pt-[50px]">
                <div className="flex gap-[5px] flex-col justify-center items-center">
                    {subtitle && <h5 className="text-[15px] font-[500] text-[#0000ED]">{subtitle}</h5>}

                    <h1 className="text-[30px] font-[700] text-center">
                        {title.replace(/\\n/g, '\n').split('\n').map((line, lineIndex) => {
                            const segments = line.split(/(\d{2})/g);
                            return (
                                <span key={lineIndex} className="block">
                  {segments.map((segment, segmentIndex) => {
                      const isTwoDigitNumber = /^\d{2}$/.test(segment);
                      return (
                          <span
                              key={`${lineIndex}-${segmentIndex}`}
                              className={isTwoDigitNumber ? 'text-[#0000ED]' : undefined}
                          >
                        {segment}
                      </span>
                      );
                  })}
                </span>
                            );
                        })}
                    </h1>
                </div>

            </div>

            <MobileSearchOverlay
                open={searchOpen}
                query={searchQuery}
                results={filteredResults}
                onQueryChange={setSearchQuery}
                onSelect={handleSelectResult}
                onClose={handleCloseSearch}
                inputRef={searchInputRef}
            />
            {/* 파일 내부에 포함된 모바일 메뉴 */}
            <MobileGuideMenu open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}/>
        </>
    );
};

export default GuideMobileHeader;
